import React, {useEffect, useRef, useState} from 'react';
import './NewRecord.css';
import { Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { addMonths } from 'date-fns';
import FormDialog from '../dialog/FormDialog';
import RecordName from './RecordName';
import RecordDescription from './RecordDescription';
import RecordLicense from './RecordLicense';
import RecordFile from './RecordFile';
import RecordEndDate from './RecordEndDate';
import NewRecordFooter from './NewRecordFooter';
import { ProgressStatus } from '../../Constants';
import RecordSubtitle from './RecordSubtitle';
import useRecordModification from '../../hooks/useRecordModification';
import useNewRecordSave from '../../hooks/record/useNewRecordSave';
import RecordCollections from './RecordCollections';
import useVisibleRecords from '../../hooks/useVisibleRecords';
import useNewRecordValidation from '../../hooks/validation/record/useNewRecordValidation';
import HyButton from '../utilities/HyButton';
import PropTypes from 'prop-types';
import useCollection from '../../hooks/useCollection';
import useCollections from '../../hooks/useCollections';
import RecordSpokenLanguage from './RecordSpokenLanguage';
import RecordCollectionKeywords from './RecordCollectionKeywords';
import CollectionKeywords from '../collection/keyword/CollectionKeywords';
import ContentTypes from '../form/ContentTypes';
import ScienceDropdown from '../form/ScienceDropdown';
import RecordCollectionContentType from './RecordCollectionContentType';
import RecordChapters from './RecordChapters';
import Toggle from '../form/Toggle';
import ElementHeader from '../form/ElementHeader';
import HelpDialog from '../dialog/HelpDialog';

const emptyRecord = {
  identifier: '',
  title: '',
  description: '',
  license: '',
  selectedSeries: '',
  deletionDate: addMonths(new Date(), 12).toISOString(),
  keywords: [],
  contentType: 'Ei valintaa',
  language: '',
  chapters: {},
};

const getCollection = async (identifier) => {
    const URL = `${process.env.REACT_APP_LATAAMO_PROXY_SERVER}/api/series/${identifier}`;
    try {
        const response = await fetch(URL);
        if (response.ok) return await response.json();
        throw new Error(`Unexpected status code from ${URL}`);
    } catch (error) {
        console.error(error);
        throw error;
    }
};

const NewRecord = ({ selectedSeries = '', buttonDisabled = false, excludeFirstCollectionModification = true }) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const [addChapters, setAddChapters] = useState(false);
  const [inheritedScience, setInheritedScience] = useState(null);
  const [inheritedScienceLoadedFor, setInheritedScienceLoadedFor] = useState(null);
  const [inheritedCollection, setInheritedCollection] = useState(null);
  const [isValid, messages, validate] = useNewRecordValidation(
    ['file', 'title', 'description', 'license', 'deletionDate', 'language', 'subtitles', 'chapters']
  );
  const [send, progress, resetProgress] = useNewRecordSave();
  const [record, modificationOnChange, modified, undo] = useRecordModification({ ...emptyRecord, selectedSeries }, validate, resetProgress);
  const [_records, _loadingRecords, reloadRecords] = useVisibleRecords({});
  const [collection, _loadingCollection, reloadCollection] = useCollection();
  const [_collections, _loadingCollections, reloadCollections] = useCollections();
  const formRef = useRef();
  const [seriesModifiedCount, setSeriesModifiedCount] = useState(0);
  const [somethingModified, setSomethingModified] = useState(false);
  const seriesId = record?.selectedSeries ?? selectedSeries;

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            setInheritedCollection(null);
            if (!seriesId) {
                if (isMounted) {
                  setInheritedScience(null);
                  setInheritedScienceLoadedFor(null);
                }
                return;
            }

            try {
                const collection = await getCollection(seriesId);
                if (!isMounted) return;
                setInheritedCollection(collection);
                setInheritedScience(collection?.science ?? null);
                setInheritedScienceLoadedFor(String(seriesId));
            } catch (e) {
                if (!isMounted) return;
                setInheritedCollection(null);
                setInheritedScience(null);
                setInheritedScienceLoadedFor(String(seriesId));
            }
        };

        load();
        return () => {
            isMounted = false;
        };
    }, [seriesId]);

  const onChange = (key, value) => {
    if (key === 'selectedSeries' && excludeFirstCollectionModification) {
      setSeriesModifiedCount(seriesModifiedCount + 1);
    } else {
      setSomethingModified(true);
    }
    modificationOnChange(key, value);
  };

  const theButton = (
    <HyButton className="new-record-button" variant="primary" onClick={
      () => {
        setShowDialog(true);
      }
    }
      disabled={buttonDisabled}
    >
      {t('new_record_button')}
    </HyButton>
  );

  const onSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await send({ ...record, archivedDate: record.deletionDate }, record.subtitles);
    reloadRecords();
    reloadCollections();
  };

  const reset = async () => {
    await undo();
    setAddChapters(false);
  };

  const hide = () => {
    reset();
    setShowDialog(false);
    setSeriesModifiedCount(0);
    setSomethingModified(false);
    if (record.selectedSeries === collection?.identifier && progress.status !== ProgressStatus.NEW_RECORD.NOT_STARTED) {
      reloadCollection();
    }
  };

  const onProgressButtonClick = async () => {
    if (progress.status === ProgressStatus.NEW_RECORD.DONE) {
      if (formRef.current) {
        formRef.current.reset();
      }
      await reset();
    } else if (progress.status === ProgressStatus.NEW_RECORD.ERROR) {
      await send({ ...record, archivedDate: record.deletionDate }, record.subtitles);
    }
  };

  const closeable = progress.status !== ProgressStatus.NEW_RECORD.SENDING && progress.status !== ProgressStatus.NEW_RECORD.SENDING_SUBTITLES;
  const closeButton = closeable ? { closeButton: true } : {};

  const disabled = {
    [ProgressStatus.NEW_RECORD.SENDING]: true,
    [ProgressStatus.NEW_RECORD.SENDING_SUBTITLES]: true,
    [ProgressStatus.NEW_RECORD.PROCESSING_SUBTITLES]: true,
    [ProgressStatus.NEW_RECORD.SENDING_SUBTITLE_ORDER]: true,
    [ProgressStatus.NEW_RECORD.PROCESSING]: true,
    [ProgressStatus.NEW_RECORD.ERROR]: true,
    [ProgressStatus.NEW_RECORD.DONE]: true,
  }[progress.status] || false;

  const touched = modified
    && (somethingModified || seriesModifiedCount > 1)
    && progress.status !== ProgressStatus.NEW_RECORD.PROCESSING
    && progress.status !== ProgressStatus.NEW_RECORD.PROCESSING_SUBTITLES
    && progress.status !== ProgressStatus.NEW_RECORD.DONE;

  return (
    <FormDialog touched={touched} closeable={closeable} showComponent={theButton} show={showDialog} hide={hide}>
      <Modal.Header { ...closeButton }>{t('new_record_form_header')}</Modal.Header>
      <Form className="new-record-form" onSubmit={onSubmit} ref={formRef}>
        <Modal.Body>
            <Container className="new-record-fields ps-0">
              <Row className="mb-2">
                <Col>
                  <RecordFile onChange={(file) => onChange('file', file)} message={messages.file} disabled={disabled} />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <RecordName name={record.title} message={messages.title} onChange={(title) => onChange('title', title)} disabled={disabled} />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <RecordDescription message={messages.description} onChange={(description) => onChange('description', description)} description={record.description} disabled={disabled} />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <RecordLicense license={record.license} aria-label={t('new_record_license_label')} onChange={(license) => onChange('license', license)} message={messages.license} disabled={disabled} />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <RecordEndDate endDate={record.deletionDate} onChange={(date) => onChange('deletionDate', date)} message={messages.deletionDate} disabled={disabled} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <RecordCollections collection={record.selectedSeries} onChange={(collection) => onChange('selectedSeries', collection)} message={messages.selectedSeries} disabled={disabled} showLink={false} />
                </Col>
              </Row>
              <Row>
                <Col>
                  <ContentTypes
                    afterHeader={(
                      <div className="mb-2">
                        <RecordCollectionContentType
                          id={seriesId}
                          collection={inheritedScienceLoadedFor === String(seriesId) ? inheritedCollection : null}
                          loading={Boolean(seriesId) && inheritedScienceLoadedFor !== String(seriesId)}
                        />
                      </div>
                    )}
                    message={messages?.contentType}
                    onChange={(contentType) => onChange('contentType', contentType)}
                    disabled={disabled}
                    selected={record.contentType}
                    defaultOptionLabelKey="new_record_content_type_option_default_select"
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <ScienceDropdown
                    className="new-record-science-dropdown"
                    selected={record.science ?? ''}
                    inheritedScience={inheritedScience}
                    inheritedScienceLoading={Boolean(seriesId) && inheritedScienceLoadedFor !== String(seriesId)}
                    placeholder={t('science_option_change')}
                    required={false}
                    onChange={(science) => onChange('science', science)}
                    disabled={disabled}
                    message={messages?.science}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={12} md={6}>
                  <RecordCollectionKeywords
                    id={seriesId}
                    collection={inheritedScienceLoadedFor === String(seriesId) ? inheritedCollection : null}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <CollectionKeywords
                    keywords={record.keywords}
                    onKeywordChange={(keywords) => onChange('keywords', keywords)}
                    disabled={disabled}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <RecordSpokenLanguage
                    language={record.language}
                    onChange={(language) => onChange('language', language)}
                    disabled={disabled}
                    message={messages.language}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <RecordSubtitle
                    onChange={(subtitles) => onChange('subtitles', subtitles)}
                    subtitles={record.subtitles}
                    disabled={disabled}
                    message={messages.subtitles}
                    newRecord={true}
                    language={record.language}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Container>
                    <Form.Group>
                      <Row className="record-subtitle-row mb-2">
                        <Col>
                          <ElementHeader
                            label={t('record_chapters_header')}
                            helpDialog={(
                              <HelpDialog label={t('record_chapters_help_label')}>
                                {t('record_chapters_help_content')}
                              </HelpDialog>
                            )}
                          >
                            {t('record_chapters_header')}
                          </ElementHeader>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Toggle
                            labels={[t('record_chapters_add_checkbox')]}
                            selected={addChapters ? 0 : -1}
                            onSelect={() => {
                              const checked = !addChapters;
                              setAddChapters(checked);
                              if (!checked) onChange('chapters', {});
                            }}
                            disabled={disabled}
                          >
                            <RecordChapters
                              chapters={record.chapters}
                              onChange={(chapters) => onChange('chapters', chapters)}
                              message={messages.chapters}
                              disabled={disabled}
                            />
                          </Toggle>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Container>
                </Col>
              </Row>
            </Container>
        </Modal.Body>
        <Modal.Footer>
          <NewRecordFooter onCancel={hide} progress={progress} isValid={isValid} onClick={onProgressButtonClick} />
        </Modal.Footer>
      </Form>
    </FormDialog>
  );
};

NewRecord.propTypes = {
  selectedSeries: PropTypes.string
};

export default NewRecord;
